/**
 * WHAT ONE ROW READS AS — as a mark, not a letter to decode.
 *
 * The sheet has always carried the right information and asked the reader to assemble
 * it: a Y in the "Reads" column, and, two columns to the left, whether that Y came from
 * the record or from the nurse. Those are not the same claim. "The chart shows the
 * assessment was done" and "the reviewer says it was done" survive a survey very
 * differently, and the one thing a DON scanning eleven rows wants to know first is which
 * rows are still theirs to work.
 *
 * So each row resolves to ONE of these marks:
 *
 *   ✓  Met          the record answered it, and the answer is yes
 *   ◍  Met — you    a reviewer answered it; the record did not, or said otherwise
 *   !  Not read     nobody has answered it. NOT a failure and NOT a pass
 *   ✕  Not met      answered no — this is the gap
 *   –  N/A          the indicator does not apply to this event
 *
 * FIVE, NOT FOUR, AND THAT IS NOT SCOPE CREEP. N/A is on the facility's own paper form
 * and most of the restraint sheet is N/A when there was no restraint; folding it into any
 * of the other four would be a lie about eleven rows at a time.
 *
 * ── THE WHOLE POINT: "Not read" MUST NOT COLLAPSE INTO EITHER NEIGHBOUR ──────────────
 *
 * Several rows return neither yes nor no BY DESIGN, because the source is not readable:
 * falls row 6 when the care plan cannot be read until November 2026, falls row 10 always,
 * the diabetic consult rows and the medication rows when nothing in the chart matched a
 * name we know. Collapse those into two marks and you pick a failure mode:
 *
 *   as "Not met" → a wall of red on rows that are probably fine, which is exactly the
 *                  false-alarm noise that gets a compliance tool switched off.
 *   as "Met"     → worse. It reports compliant where nobody looked.
 *
 * Which is the house rule — two states are usually three. "We looked and found nothing",
 * "we could not look", and "nobody has looked yet" are different facts.
 *
 * ── COLOUR IS NOT THE MARK ───────────────────────────────────────────────────────────
 *
 * Every mark carries a glyph AND a word. The colour is the fourth signal, not the first,
 * because this document gets photocopied in black and white and handed to a surveyor, and
 * because `?` has to stay legible to a reader who cannot see the difference between the
 * amber and the red. The print sheet renders letters for the same reason.
 *
 * Deliberately a plain module with no React in it: the header's percentage and the row's
 * mark have to agree about who answered, and the only way to guarantee that is for both
 * to call the same function. They used to each carry their own copy of the ternary.
 */

export const MARK = {
  MET_DATA: "MET_DATA",
  MET_PERSON: "MET_PERSON",
  NOT_MET: "NOT_MET",
  NOT_READ: "NOT_READ",
  NOT_APPLICABLE: "NOT_APPLICABLE",
};

/**
 * `tone` is the on-screen palette. `NOT_READ` is amber and not the cool blue the record
 * chip uses, and the difference is deliberate: the record chip states what the
 * INTEGRATION holds, which is not a call to action, while this column states what the
 * ROW reads as, and an unread row is the reader's next piece of work. It is still not a
 * failure — `counts: false` keeps it out of the percentage entirely — and the word on the
 * mark says so.
 */
const MARKS = {
  [MARK.MET_DATA]: {
    mark: MARK.MET_DATA,
    glyph: "✓",
    word: "Met",
    by: "from the record",
    title: "Met — answered from the chart, no reviewer entry needed",
    tone: { bg: "#dcfce7", fg: "#14532d", border: "#86efac" },
    counts: true,
    met: true,
  },
  [MARK.MET_PERSON]: {
    mark: MARK.MET_PERSON,
    glyph: "◍",
    word: "Met — you",
    by: "entered by a reviewer",
    title: "Met — a reviewer answered this; it is not read from the chart",
    tone: { bg: "#ecfdf5", fg: "#065f46", border: "#6ee7b7" },
    counts: true,
    met: true,
  },
  [MARK.NOT_MET]: {
    mark: MARK.NOT_MET,
    glyph: "✕",
    word: "Not met",
    by: null,
    title: "Not met — this is a gap the sheet is asserting",
    tone: { bg: "#fee2e2", fg: "#7f1d1d", border: "#fca5a5" },
    counts: true,
    met: false,
  },
  [MARK.NOT_READ]: {
    mark: MARK.NOT_READ,
    glyph: "!",
    word: "Not read",
    by: null,
    title: "Could not be read — not a pass and not a gap. Open the row for why",
    tone: { bg: "#fef3c7", fg: "var(--ed-amber-deep)", border: "#fcd34d" },
    counts: false,
    met: null,
  },
  [MARK.NOT_APPLICABLE]: {
    mark: MARK.NOT_APPLICABLE,
    glyph: "–",
    word: "N/A",
    by: null,
    title: "Does not apply to this event",
    tone: { bg: "var(--ed-line-2)", fg: "var(--ed-ink-3)", border: "var(--ed-line-hover)" },
    counts: false,
    met: null,
  },
};

/** The five marks in the order they are explained to a reader. */
export const MARK_ORDER = [
  MARK.MET_DATA,
  MARK.MET_PERSON,
  MARK.NOT_READ,
  MARK.NOT_MET,
  MARK.NOT_APPLICABLE,
];

export const markOf = (mark) => MARKS[mark] || MARKS[MARK.NOT_READ];

/**
 * WHO ANSWERED THE ROW. `null` means nobody did.
 *
 * A system row whose own state is '?' has NOT answered it — it has reported that it could
 * not. That distinction is the difference between a percentage and a guess, so it lives
 * here once rather than in each caller's ternary.
 */
export function answeredBy({ entered = null, systemState = null } = {}) {
  if (entered) return "person";
  if (systemState && systemState !== "?") return "data";
  return null;
}

/** The row's answer: the reviewer's where they gave one, otherwise the record's. */
export function effectiveState({ entered = null, systemState = null } = {}) {
  return entered || systemState || "?";
}

/**
 * The mark one row reads as.
 *
 * UNRECOGNISED INPUT FAILS TO "Not read", NEVER TO "Met". A state this build does not
 * know — a typo, a future letter, a null that got this far — is a thing nobody has
 * answered, and the only safe way to render it is as the reader's work. The alternative
 * is a tool that reports compliance for a value it could not parse.
 */
export function rowMark({ entered = null, systemState = null } = {}) {
  const state = effectiveState({ entered, systemState });
  const by = answeredBy({ entered, systemState });

  if (state === "NA") return MARKS[MARK.NOT_APPLICABLE];
  if (state === "N") return MARKS[MARK.NOT_MET];
  if (state === "Y") return by === "person" ? MARKS[MARK.MET_PERSON] : MARKS[MARK.MET_DATA];
  return MARKS[MARK.NOT_READ];
}
